import { useEffect, useState } from 'react';
import { assetsApi } from '@/lib/api';
import { useAuth } from '@/contexts/auth-context';
import type { Asset } from '@/types/entities';
import { AssetsGrid } from '@/components/shared/assets-grid';
import { AssetPickerDialog } from '@/components/shared/asset-picker-dialog';
import { ConfirmationDialog } from '@/components/custom/confirmation-dialog';
import { Button } from '@/components/ui/button';
import { PageHeader, PageSection } from '@/components/layouts/page-layout';

const PAGE_SIZE = 12;

export function TenantAssetsPage() {
  const { user } = useAuth();
  const tenant = user?.tenant;
  
  const [assets, setAssets] = useState<Asset[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assetPickerOpen, setAssetPickerOpen] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState<Asset | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(assets.length / PAGE_SIZE));
  const pageStart = Math.max(1, currentPage - 2);
  const pageEnd = Math.min(totalPages, currentPage + 2);
  const pageNumbers = Array.from(
    { length: pageEnd - pageStart + 1 },
    (_, index) => pageStart + index,
  );
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const paginatedAssets = assets.slice(startIndex, startIndex + PAGE_SIZE);

  const fetchAssets = async () => {
    if (!tenant?.slug) {
      setAssets([]);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const data = await assetsApi.getAllByTenantSlug(tenant.slug);
      setAssets(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load assets');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAssets();
  }, [tenant]);

  useEffect(() => {
    setCurrentPage(1);
  }, [tenant?.slug]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleUpload = async (nameInput: string, file: File) => {
    if (!tenant?.slug) return;

    setIsUploading(true);
    setError(null);
    try {
      await assetsApi.upload(tenant.slug, file, nameInput || undefined);
      fetchAssets();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload asset');
      throw err;
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = (asset: Asset) => {
    setAssetToDelete(asset);
  };

  const confirmDelete = async () => {
    if (!tenant?.slug || !assetToDelete) return;

    setIsDeleting(true);
    setError(null);
    try {
      await assetsApi.deleteByTenantSlug(tenant.slug, assetToDelete.id);
      setAssetToDelete(null);
      fetchAssets();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete asset');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Assets"
        description="Upload and manage files for this tenant."
        actions={
          tenant?.slug ? (
            <Button onClick={() => setAssetPickerOpen(true)}>
              Upload Asset
            </Button>
          ) : null
        }
      />

      <PageSection>
        {error && (
          <div className="rounded-md bg-destructive/10 p-3 text-destructive">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="text-muted-foreground">Loading assets...</div>
        ) : tenant?.slug ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{assets.length} asset{assets.length === 1 ? '' : 's'}</span>
              <span>Page {currentPage} of {totalPages}</span>
            </div>

            <AssetsGrid assets={paginatedAssets} onDelete={handleDelete} tenantSlug={tenant.slug} isDeleting={isDeleting} />

            {assets.length > PAGE_SIZE && (
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                {pageNumbers.map((pageNumber) => (
                  <Button
                    key={pageNumber}
                    type="button"
                    variant={currentPage === pageNumber ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCurrentPage(pageNumber)}
                  >
                    {pageNumber}
                  </Button>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-muted-foreground">Select a tenant to view assets.</div>
        )}
      </PageSection>

      <AssetPickerDialog
        open={assetPickerOpen}
        onOpenChange={setAssetPickerOpen}
        onUpload={handleUpload}
        isUploading={isUploading}
        error={error}
      />

      <ConfirmationDialog
        open={!!assetToDelete}
        onOpenChange={(open) => !open && setAssetToDelete(null)}
        title="Delete Asset"
        description={`Are you sure you want to delete "${assetToDelete?.name}"? This will remove the file and unlink it from content.`}
        onConfirm={confirmDelete}
        confirmVariant="destructive"
        confirmLabel={isDeleting ? 'Deleting...' : 'Delete'}
        confirmDisabled={isDeleting}
        cancelDisabled={isDeleting}
      />
    </>
  );
}
